export interface Action<PayloadType> {
    id: number;
    payload: PayloadType;
}

export interface AgentActionPayload {
    textual: {
        text: string;
    };
    chunks?: Chunk[];
    media?: MediaAction;
}

interface MediaAction {
    attachments: MediaAttachment[];
}

export interface AgentUtteranceAction extends Action<AgentActionPayload> {
    action_type: 'utterance';
}

export interface UserUtteranceActionPayload {
    text: string;
}

export interface UserUtteranceAction
    extends Action<UserUtteranceActionPayload> {
    action_type: 'utterance';
}

export interface UserShareMaterialsActionPayload {
    action: 'open' | 'share_start' | 'share_stop';
    name: string;
    url: string;
}
export interface UserShareMaterialsAction
    extends Action<UserShareMaterialsActionPayload> {
    action_type: 'share_materials';
}

export interface UserResumeAction extends Action<unknown> {
    action_type: 'resume';
}


export interface Event<
    UserActionType = null | UserUtteranceAction | UserShareMaterialsAction| UserResumeAction,
    AgentActionType = null | AgentUtteranceAction
> {
    created_at: string;
    created_at_delta?: number;
    agent_action: AgentActionType;
    user_action: UserActionType;
}

export const isResumeUserEvent = (event: Event): event is Event<UserResumeAction, null> => {
    return event.user_action?.action_type === 'resume';
};

export const isUserUtteranceEvent = (event: Event): event is Event<UserUtteranceAction, null> => {
    return (
        event.user_action?.action_type === 'utterance' &&
        Boolean(event.user_action.payload.text)
    );
};

export const isUserMaterialsShareStartEvent = (event: Event): event is Event<UserShareMaterialsAction, null> => {
    return (
        event.user_action?.action_type === 'share_materials' &&
        event.user_action.payload.action === 'share_start'
    );
};

export const isAgentUtteranceEvent = (event: Event): event is Event<null, AgentUtteranceAction> => {
    return (
        event.agent_action?.action_type === 'utterance' &&
        Boolean(event.agent_action.payload.textual?.text)
    );
}

interface Chunk {
    html: string;
    text: string;
    citations?: Citation[];
    footnotes?: Footnote[];
}

export interface Citation {
    id: number;
    title: string;
    content: string;
    reference: string;
    media: CitationMedia[];
    highlighted_span: [number, number];
    metadata?: {
        collection?: {
            collection_id: string;
            index: number;
        };
    };
    should_open_sources?: boolean;
}

export interface CitationMedia {
    caption: string;
    description: string;
    type: string;
    url: string;
}

export interface Footnote {
    id: number;
    symbol: string;
    content: string;
    should_open_sources?: boolean;
}

export type AgentMessageFormat = 'markdown' | 'html';

export type AgentTextMessage = {
    type: 'agent';
    actionId: number;
    text: string[];
    attachments?: MediaAttachment[];
    citations?: Citation[];
    footnotes?: Footnote[];
    className?: string;
};

interface MediaAttachment {
    url: string;
    name?: string;
    kind: string;
    description?: string;
    metadata?: {
        collection?: {
            collection_id: string;
            index: number;
        };
    };
}